import React, { useState, useEffect, useRef } from "react";
import { Button, Box, CircularProgress, Typography } from "@mui/material";
import { getIdToken, signInWithPhoneNumber, RecaptchaVerifier } from "firebase/auth";
import Cookies from "universal-cookie";
import { auth } from "../../firebase";
import config from "../../config.js";

const API_URL = `${config.baseURL}/auth/login?type=firebase`;

const OtpVerification = ({
  phoneNumber,
  confirmationResult,
  loading,
  setLoading,
  onSuccess,
  onError,
  handleBackToLogin,
  dispatch,
  navigate,
  resetAuthState,
  setConfirmationResult,
}) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timerCount, setTimerCount] = useState(45);
  const [timerActive, setTimerActive] = useState(true);
  const [canResend, setCanResend] = useState(false);
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isRecaptchaVerified, setIsRecaptchaVerified] = useState(false);
  const inputRefs = useRef([]);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);
  const recaptchaVerifierRef = useRef(null);
  const MAX_VERIFICATION_ATTEMPTS = 3;

  const isOtpComplete = otp.every((digit) => digit !== "");

  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (e) {
          //console.warn("Error clearing reCAPTCHA:", e);
        }
        recaptchaVerifierRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (timerActive && timerCount > 0) {
      timerRef.current = setTimeout(() => {
        if (mountedRef.current) setTimerCount((prev) => prev - 1);
      }, 1000);
    } else if (timerCount === 0) {
      if (mountedRef.current) {
        setCanResend(true);
        setTimerActive(false);
      }
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [timerActive, timerCount]);

  useEffect(() => {
    if (inputRefs.current[0]) inputRefs.current[0].focus();
  }, []);

  const handleOtpChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 1) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (e.nativeEvent.inputType === "insertFromPaste") {
        const pastedText = e.clipboardData?.getData("text") || "";
        const digits = pastedText.replace(/\D/g, "").split("").slice(0, 6);
        if (digits.length > 0) {
          const newFullOtp = [...otp];
          digits.forEach((digit, idx) => {
            if (index + idx < 6) newFullOtp[index + idx] = digit;
          });
          setOtp(newFullOtp);
          const nextEmptyIndex = newFullOtp.findIndex((d) => d === "");
          if (nextEmptyIndex !== -1 && nextEmptyIndex < 6) {
            inputRefs.current[nextEmptyIndex].focus();
          } else if (digits.length + index < 6) {
            inputRefs.current[digits.length + index].focus();
          } else {
            inputRefs.current[5].focus();
          }
          return;
        }
      }

      if (value !== "" && index < 5) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index] === "" && index > 0) {
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
        inputRefs.current[index - 1].focus();
      } else if (otp[index] !== "") {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1].focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    const digits = pastedData.replace(/\D/g, "").slice(0, 6).split("");
    if (digits.length) {
      const newOtp = [...otp];
      digits.forEach((digit, idx) => {
        if (idx < 6) newOtp[idx] = digit;
      });
      setOtp(newOtp);
      const nextEmptyIndex = newOtp.findIndex((d) => d === "");
      if (nextEmptyIndex !== -1 && nextEmptyIndex < 6) {
        inputRefs.current[nextEmptyIndex].focus();
      } else {
        inputRefs.current[5].focus();
      }
    }
  };

  const handleVerify = async () => {
    if (!isOtpComplete || !confirmationResult || isVerifying) return;

    if (verificationAttempts >= MAX_VERIFICATION_ATTEMPTS) {
      onError("Too many failed attempts. Please request a new OTP.");
      return;
    }

    setIsVerifying(true);
    setLoading(true);
    const enteredOtp = otp.join("");

    try {
      //console.log("Starting OTP verification");
      const confirmResult = await confirmationResult.confirm(enteredOtp);
      const user = confirmResult.user;
      //console.log("Retrieving ID token");
      const token = await getIdToken(user);
      //console.log("ID token retrieved");

      sessionStorage.setItem("authToken", token);

      //console.log("Sending backend request");
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ firebaseToken: token }),
        credentials: "include",
      });
      //console.log("Backend response received");

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Backend verification failed");
      }

      const data = await response.json();
      dispatch({
        type: "SET_USER",
        payload: {
          ...data.tokenUser.tokenuser,
          role: data.tokenUser.userRoleWithAccess.name,
        },
      });
      dispatch({
        type: "SET_FEATURES",
        payload: data.tokenUser.userRoleWithAccess.features.map((feature) => ({
          action: feature.feature.action,
          api_path: feature.feature.api_path,
          name: feature.feature.name,
          description: feature.feature.description,
          isDefault: feature.feature.isDefault,
          displayName: feature.feature.displayName,
        })),
      });

      const cookies = new Cookies();
      cookies.set("token", token, {
        path: "/",
        secure: true,
        sameSite: "strict",
        expires: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      });

      setOtp(["", "", "", "", "", ""]);
      if (mountedRef.current && typeof onSuccess === "function") {
        onSuccess("Phone verification successful!");
      }
    } catch (error) {
      //console.error("OTP verification error:", error);
      setVerificationAttempts((prev) => prev + 1);

      let errorMessage = "Invalid OTP. Please try again.";
      if (error.code === "auth/code-expired") {
        errorMessage = "OTP has expired. Please request a new code.";
        setCanResend(true);
        setTimerActive(false);
        setTimerCount(0);
      } else if (error.code === "auth/invalid-verification-code") {
        errorMessage = `Invalid OTP. ${MAX_VERIFICATION_ATTEMPTS - verificationAttempts - 1} attempts remaining.`;
      } else if (error.code === "auth/too-many-requests") {
        errorMessage = "Too many attempts. Please try again later.";
      } else if (error.code === "auth/network-request-failed") {
        errorMessage = "Network error. Please check your connection and try again.";
      } else if (error.message.includes("Backend verification failed")) {
        errorMessage = "Authentication failed. Please try again later.";
      }

      sessionStorage.removeItem("authToken");
      if (typeof onError === "function" && mountedRef.current) {
        onError(errorMessage);
      }

      setOtp(["", "", "", "", "", ""]);
      if (inputRefs.current[0]) inputRefs.current[0].focus();
    } finally {
      if (mountedRef.current) {
        setIsVerifying(false);
        setLoading(false);
      }
    }
  };

  const handleResendOtp = debounce(async () => {
    if (!canResend || isResending || isRecaptchaVerified) return;

    setIsResending(true);
    setLoading(true);

    try {
      const formattedPhone = phoneNumber.trim().replace(/[\s()-]/g, "");
      //console.log("Resending OTP to:", formattedPhone);

      // Initialize visible reCAPTCHA for resend
      recaptchaVerifierRef.current = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "normal", // Visible reCAPTCHA widget
        callback: () => {
          //console.log("reCAPTCHA verified successfully for resend");
          setIsRecaptchaVerified(true);
        },
        "expired-callback": () => {
          //console.warn("reCAPTCHA expired for resend");
          setIsRecaptchaVerified(false);
          if (mountedRef.current) {
            onError("Security verification expired. Please verify again.");
          }
        },
        "error-callback": (error) => {
          //console.error("reCAPTCHA error for resend:", error);
          setIsRecaptchaVerified(false);
          if (mountedRef.current) {
            onError("Security verification failed. Please try again or disable ad blockers.");
          }
        },
      });

      try {
        await recaptchaVerifierRef.current.render();
        //console.log("reCAPTCHA rendered for resend");
      } catch (renderError) {
        //console.error("Failed to render reCAPTCHA for resend:", renderError);
        setIsRecaptchaVerified(false);
        throw new Error("Failed to initialize security verification.");
      }
    } catch (error) {
      //console.error("Error initializing reCAPTCHA for resend:", error);
      let errorMsg = "Failed to load security verification. Please try again.";
      if (error.message.includes("Failed to initialize security verification")) {
        errorMsg = "Security verification failed to initialize. Please try again.";
      }
      if (typeof onError === "function" && mountedRef.current) {
        onError(errorMsg);
      }
      setIsResending(false);
      setLoading(false);
      return;
    }
  }, 500);

  const handleResendOtpSubmit = async () => {
    if (!isRecaptchaVerified || isResending) return;

    setIsResending(true);
    setLoading(true);

    try {
      const formattedPhone = phoneNumber.trim().replace(/[\s()-]/g, "");
      //console.log("Submitting OTP resend to:", formattedPhone);

      const newConfirmationResult = await signInWithPhoneNumber(
        auth,
        formattedPhone,
        recaptchaVerifierRef.current
      );
      if (!mountedRef.current) return;

      if (typeof setConfirmationResult === "function") {
        setConfirmationResult(newConfirmationResult);
      }

      setOtp(["", "", "", "", "", ""]);
      setVerificationAttempts(0);
      setTimerCount(45);
      setTimerActive(true);
      setCanResend(false);
      setIsRecaptchaVerified(false); // Reset for next use

      if (inputRefs.current[0]) inputRefs.current[0].focus();

      if (typeof onSuccess === "function" && mountedRef.current) {
        onSuccess(`New verification code sent to ${formattedPhone}`);
      }
    } catch (error) {
      //console.error("Error in OTP resend process:", error);
      let errorMsg = "Failed to send verification code. Please try again.";
      if (error.code === "auth/network-request-failed") {
        errorMsg = "Network error. Please check your connection and try again.";
      } else if (error.code === "auth/invalid-phone-number") {
        errorMsg = "Invalid phone number. Please check the format and try again.";
      } else if (error.code === "auth/too-many-requests") {
        errorMsg = "Too many attempts. Please try again later.";
      } else if (error.code === "auth/captcha-check-failed") {
        errorMsg = "Security verification failed. Please try again or disable ad blockers.";
      }
      if (typeof onError === "function" && mountedRef.current) {
        onError(errorMsg);
      }
      setIsRecaptchaVerified(false); // Reset to allow re-verification
    } finally {
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (e) {
          //console.warn("Error clearing reCAPTCHA:", e);
        }
        recaptchaVerifierRef.current = null;
      }
      if (mountedRef.current) {
        setIsResending(false);
        setLoading(false);
      }
    }
  };

  const handleBackToLoginWithReset = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (typeof resetAuthState === "function") {
      resetAuthState();
    }
    handleBackToLogin();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins < 10 ? "0" : ""}${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  return (
    <div style={{ padding: "1rem", width: "100%" }}>
      <div style={{ textAlign: "center", marginBottom: "1rem" }}>
        <h2 style={{ fontSize: "1.25rem", fontWeight: "500", marginBottom: "0.25rem" }}>
          Enter the OTP
        </h2>
        <p style={{ fontSize: "0.875rem", color: "#666", marginTop: "0.25rem" }}>
          We've sent a 6-digit code to {phoneNumber}
        </p>
      </div>
      <div
        style={{ display: "flex", justifyContent: "space-between", marginBottom: "1.5rem", gap: "0.5rem" }}
        onPaste={handlePaste}
      >
        {[0, 1, 2, 3, 4, 5].map((index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            maxLength="1"
            value={otp[index]}
            onChange={(e) => handleOtpChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            style={{
              width: "40px",
              height: "48px",
              textAlign: "center",
              fontSize: "20px",
              fontWeight: "500",
              border: "1px solid #ccc",
              borderRadius: "8px",
              outline: "none",
              caretColor: "transparent",
            }}
            disabled={loading || isVerifying || isResending}
            autoComplete="one-time-code"
          />
        ))}
      </div>
      <Button
        onClick={handleVerify}
        disabled={!isOtpComplete || loading || isVerifying || isResending || verificationAttempts >= MAX_VERIFICATION_ATTEMPTS}
        variant="contained"
        style={{
          width: "100%",
          borderRadius: "16px",
          marginTop: "20px",
          backgroundColor: isOtpComplete && verificationAttempts < MAX_VERIFICATION_ATTEMPTS ? "#FFFF00" : "#E0E0E0",
          color: isOtpComplete && verificationAttempts < MAX_VERIFICATION_ATTEMPTS ? "#000000" : "#999999",
          boxShadow: "none",
          fontWeight: "600",
          fontSize: "18px",
          textTransform: "none",
          height: "48px",
          position: "relative",
        }}
      >
        {(loading || isVerifying) ? (
          <CircularProgress size={24} sx={{ color: "#000000", position: "absolute" }} />
        ) : (
          "Verify"
        )}
      </Button>
      <div style={{ textAlign: "center", marginTop: "1rem" }}>
        {!canResend ? (
          <span style={{ fontSize: "14px", color: "#666" }}>
            Resend OTP in {formatTime(timerCount)}
          </span>
        ) : (
          <>
            <span style={{ fontSize: "14px", color: "#666" }}>
              Didn't receive the code?
            </span>
            <div style={{ marginTop: "0.5rem" }}>
              <Typography variant="body2" style={{ marginBottom: "0.5rem" }}>
                Please verify you're not a robot
              </Typography>
              <div id="recaptcha-container" />
            </div>
            <Button
              onClick={handleResendOtpSubmit}
              disabled={loading || isVerifying || isResending || !isRecaptchaVerified}
              variant="contained"
              style={{
                marginTop: "0.5rem",
                backgroundColor: isRecaptchaVerified ? "#FFFF00" : "#E0E0E0",
                color: isRecaptchaVerified ? "#000000" : "#999999",
                fontSize: "14px",
                fontWeight: "500",
                borderRadius: "8px",
                textTransform: "none",
                padding: "8px 16px",
              }}
            >
              {isResending ? "Sending..." : "Resend OTP"}
            </Button>
          </>
        )}
      </div>
      <Box sx={{ marginTop: "20px", textAlign: "center" }}>
        <Button
          onClick={handleBackToLoginWithReset}
          disabled={loading || isVerifying || isResending}
          style={{ color: "#20A8E0", textTransform: "none", fontSize: "16px" }}
        >
          Back to Login
        </Button>
      </Box>
    </div>
  );
};

export default OtpVerification;